/**
 * Rhizome — the collective ending. The lineage graph of fragments: node size =
 * vitality, colour = tactic, edges = derivation. Laid out with d3-force (run to
 * settle, then rendered as static SVG so it exports to PNG and is deterministic).
 *
 * The plan named d3-force-3d + r3f; this ships the 2D SVG variant deliberately —
 * fewer moving parts, guaranteed offline, and it reads as a map. A 3D r3f
 * rhizome remains a drop-in later.
 */

import { useMemo, useRef } from 'react';
import {
  forceCenter,
  forceCollide,
  forceLink,
  forceManyBody,
  forceSimulation,
  type SimulationLinkDatum,
  type SimulationNodeDatum,
} from 'd3-force';
import { rhizomeStats, type Rhizome as RhizomeData, type Tactic } from '@wintermute/orchestrator';
import { TACTIC_COLOR, TACTIC_LABEL } from './tactics.js';
import { downloadSvgAsPng } from '../capture/capture.js';

interface Props {
  rhizome: RhizomeData;
}

interface Node extends SimulationNodeDatum {
  id: string;
  tactic: Tactic;
  vitality: number;
  generation: number;
}
interface Link extends SimulationLinkDatum<Node> {
  op: string | null;
}

const W = 820;
const H = 560;

export function Rhizome({ rhizome }: Props) {
  const svgRef = useRef<SVGSVGElement>(null);
  const stats = useMemo(() => rhizomeStats(rhizome), [rhizome]);

  const { nodes, links } = useMemo(() => {
    const ns: Node[] = rhizome.nodes.map((f) => ({
      id: f.id,
      tactic: f.tactic,
      vitality: f.vitality,
      generation: f.generation,
    }));
    const index = new Map(ns.map((n) => [n.id, n]));
    const ls: Link[] = rhizome.edges
      .filter((e) => index.has(e.from) && index.has(e.to))
      .map((e) => ({ source: e.from, target: e.to, op: e.op }));

    const sim = forceSimulation<Node>(ns)
      .force('charge', forceManyBody<Node>().strength(-90))
      .force('link', forceLink<Node, Link>(ls).id((d) => d.id).distance(46).strength(0.7))
      .force('center', forceCenter(W / 2, H / 2))
      .force('collide', forceCollide<Node>().radius((d) => 6 + d.vitality * 10))
      .stop();

    // Run to settle synchronously.
    const iters = Math.min(400, 80 + ns.length * 4);
    for (let i = 0; i < iters; i++) sim.tick();

    return { nodes: ns, links: ls };
  }, [rhizome]);

  return (
    <div className="ending-panel">
      <svg ref={svgRef} viewBox={`0 0 ${W} ${H}`} className="rhizome-svg" role="img" aria-label="The collective rhizome">
        <rect x={0} y={0} width={W} height={H} fill="#05060a" />

        {links.map((l, i) => {
          const s = l.source as Node;
          const t = l.target as Node;
          return (
            <line
              key={i}
              x1={s.x ?? W / 2}
              y1={s.y ?? H / 2}
              x2={t.x ?? W / 2}
              y2={t.y ?? H / 2}
              stroke="rgba(255,255,255,0.14)"
              strokeWidth={1}
            />
          );
        })}

        {nodes.map((n) => (
          <circle
            key={n.id}
            cx={n.x ?? W / 2}
            cy={n.y ?? H / 2}
            r={4 + n.vitality * 10}
            fill={TACTIC_COLOR[n.tactic]}
            fillOpacity={0.35 + n.vitality * 0.6}
            stroke={n.generation === 0 ? '#e7e9f0' : 'none'}
            strokeWidth={n.generation === 0 ? 1.5 : 0}
          />
        ))}

        {/* legend */}
        {(Object.keys(TACTIC_COLOR) as Tactic[]).map((t, i) => (
          <g key={t} transform={`translate(${16}, ${20 + i * 20})`}>
            <circle cx={6} cy={-4} r={6} fill={TACTIC_COLOR[t]} />
            <text x={18} y={0} fill="#9aa0b4" fontSize={12} fontFamily="system-ui">
              {TACTIC_LABEL[t]}
            </text>
          </g>
        ))}

        <text x={W - 16} y={H - 16} textAnchor="end" fill="#6b7185" fontSize={12} fontFamily="system-ui">
          {stats.total} fragments · {stats.thriving} thriving · {stats.dead} decayed · depth {stats.maxGeneration}
        </text>
      </svg>

      <div className="ending-actions">
        <button onClick={() => svgRef.current && downloadSvgAsPng(svgRef.current, 'wintermute-rhizome.png')}>
          Save rhizome as image
        </button>
      </div>
    </div>
  );
}
